import React, { useState, useContext, useEffect } from 'react';
import { RouteComponentProps, useHistory } from 'react-router';
import { useQuery, useMutation } from '@apollo/client/react/hooks';
import { move } from 'formik';
import Styled from 'styled';
import { FormattedMessage } from 'react-intl';

import CollectionView from 'lib/collection/collection-view';
import ImageView from 'lib/images/image-view';

import LoadingWrapperView from 'four-nets/ui/loaders/loading-wrapper-view';
import Unauthorized from 'four-nets/errors/401/unauthorized';
import { ViewerContext } from 'four-nets/user/viewer/viewer';
import ErrorPage from 'four-nets/errors/error-page';
import { ImageEdgesFragment } from 'four-nets/photoblog/posts/album/edit/image-edges.graphql';
import query, { AlbumQuery, AlbumQueryVariables } from 'four-nets/photoblog/posts/album/album-query.graphql';

import ImagesEditHeader from '../images-edit-header/images-edit-header';
import mutation, { ReorderAlbumImagesMutation, ReorderAlbumImagesMutationVariables } from './images-reorder.graphql';
import defaultStyles from './images-reorder.scss';
import useWriteImagesCache from '../../use-write-images-cache/use-write-images-cache';
import { FIRST } from '../../detail/detail-loader';

type Edges = NonNullable<NonNullable<ImageEdgesFragment>['edges']>;
type Edge = Edges[0];

interface Params {
    slug: string;
    username: string;
}

export interface Props extends Partial<RouteComponentProps<Params>> {
    styles: typeof defaultStyles;
    excludeHeader?: boolean;
    onClose?: () => void;
}

const ImagesReorder: React.FC<Props> = props => {
    const { viewer } = useContext(ViewerContext);
    const history = useHistory();

    const writeImagesCache = useWriteImagesCache();

    const { styles, excludeHeader, onClose } = props;
    const { username, slug } = props.match!.params!;

    const [images, setImages] = useState<Edges>([]);
    const [dirty, setDirty] = useState(false);

    const { data, error, loading } = useQuery<AlbumQuery, AlbumQueryVariables>(query, {
        variables: { slug, username },
        fetchPolicy: 'cache-first',
        notifyOnNetworkStatusChange: true,
    });
    const [reorderAlbumImages, { loading: submitting }] = useMutation<
        ReorderAlbumImagesMutation,
        ReorderAlbumImagesMutationVariables
    >(mutation);

    useEffect(() => {
        if (data && data.postBySlug && data.postBySlug.images && data.postBySlug.images.edges) {
            setImages(data.postBySlug.images.edges);
        }
    }, [data]);

    const moveFn = (from: number, to: number) => {
        if (!dirty) {
            setDirty(true);
        }
        setImages(move(images, from, to));
    };

    const itemKey = (edge: Edge) => edge && edge.node && edge.node.id;

    const element = (edge: Edge) => {
        return (
            <div className={styles.image}>
                <ImageView
                    aspectFill={true}
                    src={edge && edge.node && edge.node.photoFile!.src}
                    srcSet={edge && edge.node && edge.node.photoFile!.srcset}
                    sizes={{
                        xs: 100,
                    }}
                />
            </div>
        );
    };

    if (!viewer || username !== viewer.username) {
        return <Unauthorized />;
    }

    const onSubmit = () => {
        if (data && data.postBySlug && data.postBySlug.id) {
            return reorderAlbumImages({
                variables: {
                    input: { id: data.postBySlug.id, images: images.map(edge => edge!.node!.id) },
                },
            }).then(result => {
                if (result.data?.reorderAlbumImages?.post?.images?.edges.length) {
                    writeImagesCache(
                        result.data.reorderAlbumImages.post.id,
                        result.data.reorderAlbumImages.post.images.edges
                    );
                }
            });
        }
        return Promise.resolve() as any;
    };

    return (
        <>
            {excludeHeader ? null : (
                <ImagesEditHeader
                    disabled={loading || submitting || !dirty}
                    dirty={dirty}
                    onSubmit={() => {
                        onSubmit().then(() => {
                            if (onClose) {
                                onClose();
                            }

                            history.push({
                                pathname: data! && data!.postBySlug! && data!.postBySlug!.href,
                                state: { ommitFirst: true },
                            });
                        });
                    }}
                    {...(data! && data!.postBySlug!)}
                />
            )}
            <LoadingWrapperView loading={loading || submitting}>
                <ErrorPage error={error}>
                    <div className={styles.wrapper}>
                        <CollectionView
                            center={false}
                            items={images}
                            element={element}
                            move={moveFn}
                            isDraggable={() => true}
                            width={100}
                            height={100}
                            itemKey={itemKey}
                        />
                    </div>
                </ErrorPage>
            </LoadingWrapperView>
            {excludeHeader ? (
                <button
                    className={styles.submitButton}
                    onClick={() => {
                        onSubmit().then(onClose);
                    }}
                >
                    <FormattedMessage id='SAVE_CHANGES' values={{ gender: 'v' }} />
                </button>
            ) : null}
        </>
    );
};

export default Styled(defaultStyles)(ImagesReorder);
